import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { BreakdownReference } from '@/types/ai-session';

export interface BreakdownQuote {
  reference: BreakdownReference;
  label: string;
  quote: string;
  /** 用户拖选的摘录，发送时放入结构化引用，不占正文预算。 */
  isExcerpt?: boolean;
  addedAt: number;
}
const sameObject = (a: BreakdownReference, b: BreakdownReference) =>
  a.sessionId === b.sessionId && a.kind === b.kind && a.entityId === b.entityId;
const sameScope = (a: BreakdownReference, b: BreakdownReference) =>
  sameObject(a, b) && a.field === b.field && a.variantDraftId === b.variantDraftId;
interface State {
  bySession: Record<string, BreakdownQuote[]>;
  locate: BreakdownReference | null;
  add: (reference: BreakdownReference, label: string, quote?: string) => boolean;
  addMany: (items: Omit<BreakdownQuote, 'addedAt'>[]) => boolean;
  remove: (sessionId: string, index: number) => void;
  removeSent: (
    sessionId: string,
    references: BreakdownReference[],
    snapshot?: BreakdownQuote[]
  ) => void;
  requestLocate: (reference: BreakdownReference) => void;
}
export const useBreakdownReferencesStore = create<State>()(
  persist(
    (set, get) => ({
      bySession: {},
      locate: null,
      add: (reference, label, quote = '') => get().addMany([{ reference, label, quote }]),
      addMany: (items) => {
        if (!items.length) return true;
        const sessionId = items[0].reference.sessionId;
        if (items.some((item) => item.reference.sessionId !== sessionId)) return false;
        let next = get().bySession[sessionId] ?? [];
        for (const { reference, label, quote, isExcerpt } of items) {
          next = next.filter(
            ({ reference: r }) =>
              !sameObject(r, reference) ||
              ((r.field || r.variantDraftId) &&
                (reference.field || reference.variantDraftId) &&
                (r.variantDraftId !== reference.variantDraftId ||
                  (r.field && reference.field && !sameScope(r, reference))))
          );
          next = [
            ...next,
            {
              reference: { ...reference },
              label,
              quote: quote.slice(0, 4000),
              ...(isExcerpt ? { isExcerpt: true } : {}),
              addedAt: Date.now(),
            },
          ];
        }
        // 跨字段选区作为一个操作提交，超限不留下部分引用。
        if (next.length > 12) return false;
        set((state) => ({ bySession: { ...state.bySession, [sessionId]: next } }));
        return true;
      },
      remove: (sessionId, index) =>
        set((state) => ({
          bySession: {
            ...state.bySession,
            [sessionId]: (state.bySession[sessionId] ?? []).filter((_, i) => i !== index),
          },
        })),
      removeSent: (sessionId, references, snapshot) =>
        set((state) => ({
          bySession: {
            ...state.bySession,
            [sessionId]: (state.bySession[sessionId] ?? []).filter(
              (item) =>
                (snapshot && !snapshot.includes(item)) ||
                !references.some(
                  (sent) =>
                    sameScope(item.reference, sent) &&
                    item.reference.artifactVersion === sent.artifactVersion
                )
            ),
          },
        })),
      requestLocate: (reference) => set({ locate: { ...reference } }),
    }),
    {
      name: 'ai-session-breakdown-references:v1',
      partialize: (state) => ({
        bySession: Object.fromEntries(
          Object.entries(state.bySession)
            .map(
              ([key, refs]) =>
                [key, refs.filter((ref) => Date.now() - ref.addedAt < 7 * 86400000)] as const
            )
            .filter(([, refs]) => refs.length)
            .slice(-50)
        ),
      }),
    }
  )
);
